// src/utils/authRedirect.js
// ─────────────────────────────────────────────────────────────
// Centralised "must be logged in" redirect helper.
// Any page (Cart, Doctor Booking, Wishlist heart, etc.) that needs
// the user to be logged in before continuing should call
// `requireLogin()` first. If the user isn't logged in, we remember
// exactly where they were (path + query + any react-router state)
// and send them to the login page. Once they log in successfully,
// `consumeRedirect()` sends them right back to that same place.
// ─────────────────────────────────────────────────────────────

const REDIRECT_KEY = "redirectAfterLogin";

/** Is someone currently logged in? */
export const isLoggedIn = () => !!localStorage.getItem("user_id");

/**
 * Call this when an action requires login.
 * Returns true if the user is already logged in (caller should proceed).
 * Returns false if it just redirected the user to the login page
 * (caller should stop / not proceed).
 */
export const requireLogin = (navigate, location, options = {}) => {
  if (isLoggedIn()) return true;

  const { message } = options;

  try {
    sessionStorage.setItem(
      REDIRECT_KEY,
      JSON.stringify({
        pathname: location?.pathname || window.location.pathname,
        search: location?.search || window.location.search,
        state: location?.state || null,
      })
    );
  } catch (e) {
    /* sessionStorage unavailable — fall back to just redirecting home */
  }

  if (message) {
    try {
      // Lazily require to avoid a hard dependency loop
      // eslint-disable-next-line global-require
      const { toast } = require("react-toastify");
      toast.info(message);
    } catch (e) {
      /* toast optional */
    }
  }

  navigate("/login-register", { state: { from: location?.pathname } });
  return false;
};

/**
 * Call this right after a successful login/registration/OTP verify.
 * Sends the user back to wherever they came from, or to `fallback`
 * (default "/") if there's nowhere remembered.
 */
export const consumeRedirect = (navigate, fallback = "/") => {
  let target = null;
  try {
    const raw = sessionStorage.getItem(REDIRECT_KEY);
    if (raw) target = JSON.parse(raw);
    sessionStorage.removeItem(REDIRECT_KEY);
  } catch (e) {
    target = null;
  }

  if (target && target.pathname) {
    navigate(
      { pathname: target.pathname, search: target.search || "" },
      { state: target.state || undefined, replace: true }
    );
  } else {
    navigate(fallback, { replace: true });
  }
};
